import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setUser } from "../redux/features/userSlice";
import Layout from "../components/Layout/Layout";
import "./Subscription.css";

const TOKEN_KEYS = ["token", "authToken", "accessToken", "jwt"];
function readToken() {
  try {
    for (const k of TOKEN_KEYS) {
      const t = localStorage.getItem(k);
      if (t && String(t).trim()) return String(t).trim();
    }
  } catch (_) {}
  return "";
}
function authHeaders() {
  const t = readToken();
  return t ? { Authorization: `Bearer ${t}`, "x-auth-token": t } : {};
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return ""; }
}

function daysLeft(iso) {
  if (!iso) return 0;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

const BADGE_ICONS = ["🥉", "🥈", "🥇"];

export default function Subscription() {
  const { user } = useSelector((s) => s.user);
  const dispatch  = useDispatch();
  const navigate  = useNavigate();

  const [plans,    setPlans]    = useState([]);
  const [status,   setStatus]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [buying,   setBuying]   = useState(null);  // planId being purchased
  const [toast,    setToast]    = useState(null);  // { type: "success"|"error", msg }
  const [confirm,  setConfirm]  = useState(null);  // plan object awaiting confirm

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4500);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, statusRes] = await Promise.all([
        axios.get("/api/subscription/plans"),
        axios.get("/api/subscription/my-status", { headers: authHeaders() }),
      ]);
      if (plansRes.data?.success) setPlans(plansRes.data.data || []);
      if (statusRes.data?.success) setStatus(statusRes.data.data);
    } catch (_) {
      showToast("error", "Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleBuy = async (plan) => {
    setConfirm(null);
    setBuying(plan._id);
    try {
      const res = await axios.post(
        "/api/subscription/buy",
        { planId: plan._id },
        { headers: authHeaders() }
      );
      if (res.data?.success) {
        showToast("success", res.data.message);
        // Refresh status and update Redux balance
        const [statusRes, userData] = await Promise.allSettled([
          axios.get("/api/subscription/my-status", { headers: authHeaders() }),
          axios.post("/api/user/getUserData", {}, { headers: authHeaders() }),
        ]);
        if (statusRes.status === "fulfilled" && statusRes.value.data?.success)
          setStatus(statusRes.value.data.data);
        if (userData.status === "fulfilled" && userData.value.data?.success)
          dispatch(setUser(userData.value.data.data.user));
      } else {
        showToast("error", res.data?.message || "Purchase failed");
      }
    } catch (e) {
      showToast("error", e?.response?.data?.message || "Purchase failed. Try again.");
    } finally {
      setBuying(null);
    }
  };

  const walletBalance = Number(user?.balance || 0);

  // Compute per-plan "can afford" and "active" state
  const extendedStatus = plans.map((p, i) => {
    const canAfford = walletBalance >= p.price;
    return { ...p, canAfford, badgeIcon: BADGE_ICONS[i] || "⭐" };
  });

  const isActive      = status?.isReseller;
  const isPermanent   = status?.isPermanent;
  const expiryDate    = status?.resellerExpiry;
  const remaining     = daysLeft(expiryDate);

  return (
    <Layout>
      <div className="subPage">
        {/* ── Toast ── */}
        {toast && (
          <div className={`subToast subToast--${toast.type}`}>
            {toast.type === "success" ? "✅" : "❌"} {toast.msg}
          </div>
        )}

        {/* ── Confirm modal ── */}
        {confirm && (
          <div className="subOverlay" onClick={() => setConfirm(null)}>
            <div className="subModal" onClick={(e) => e.stopPropagation()}>
              <h3 className="subModal__title">Confirm Purchase</h3>
              <p className="subModal__body">
                Buy <strong>{confirm.name}</strong> plan for{" "}
                <strong>₹{confirm.price}</strong>?
                <br />
                <span className="subModal__sub">
                  {confirm.durationDays} days will be added to your reseller access.
                  <br />
                  Your wallet: <strong>₹{walletBalance.toFixed(2)}</strong>
                </span>
              </p>
              <div className="subModal__actions">
                <button className="subModal__cancel" onClick={() => setConfirm(null)}>
                  Cancel
                </button>
                <button
                  className="subModal__confirm"
                  onClick={() => handleBuy(confirm)}
                  disabled={buying === confirm._id}
                >
                  {buying === confirm._id ? "Processing…" : "Confirm & Pay"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="subWrap">
          {/* ── Header ── */}
          <div className="subHeader">
            <span className="subHeader__badge">👑 Reseller Program</span>
            <h1 className="subHeader__title">Get Reseller Access</h1>
            <p className="subHeader__sub">
              Buy a reseller subscription and unlock discounted pricing on all products.
            </p>
          </div>

          {/* ── Current status card ── */}
          {!loading && (
            <div className={`subStatus ${isActive ? "subStatus--active" : "subStatus--inactive"}`}>
              {isActive ? (
                <>
                  <span className="subStatus__icon">✅</span>
                  <div className="subStatus__info">
                    <span className="subStatus__label">Reseller Active</span>
                    {isPermanent ? (
                      <span className="subStatus__value">Permanent (Admin granted)</span>
                    ) : (
                      <span className="subStatus__value">
                        Expires {fmtDate(expiryDate)} &nbsp;·&nbsp; {remaining} day{remaining !== 1 ? "s" : ""} left
                      </span>
                    )}
                  </div>
                  <span className="subStatus__chip subStatus__chip--green">Active</span>
                </>
              ) : (
                <>
                  <span className="subStatus__icon">🔒</span>
                  <div className="subStatus__info">
                    <span className="subStatus__label">No Active Subscription</span>
                    <span className="subStatus__value">
                      You're paying regular rates. Subscribe to unlock reseller pricing.
                    </span>
                  </div>
                  <span className="subStatus__chip subStatus__chip--grey">Inactive</span>
                </>
              )}
            </div>
          )}

          {/* ── Wallet balance notice ── */}
          <div className="subWallet">
            <span className="subWallet__label">Wallet Balance:</span>
            <span className="subWallet__amount">₹{walletBalance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            {walletBalance < 99 && (
              <button className="subWallet__topup" onClick={() => navigate("/add-balance")}>
                + Add Money
              </button>
            )}
          </div>

          {/* ── Plan cards ── */}
          {loading ? (
            <div className="subLoading">Loading plans…</div>
          ) : (
            <div className="subPlans">
              {extendedStatus.map((plan, idx) => {
                const isPopular = idx === 1;
                return (
                  <div
                    key={plan._id}
                    className={`subPlan ${isPopular ? "subPlan--popular" : ""}`}
                  >
                    {isPopular && <span className="subPlan__ribbon">Most Popular</span>}
                    <div className="subPlan__icon">{plan.badgeIcon}</div>
                    <h2 className="subPlan__name">{plan.name}</h2>
                    <div className="subPlan__price">
                      <span className="subPlan__currency">₹</span>
                      <span className="subPlan__amount">{plan.price}</span>
                    </div>
                    <p className="subPlan__duration">{plan.durationDays} days</p>
                    <p className="subPlan__desc">{plan.description || "Full reseller access"}</p>
                    <ul className="subPlan__features">
                      <li>✓ Reseller pricing on all products</li>
                      <li>✓ Instant activation</li>
                      <li>✓ {isActive && !isPermanent ? "Extends current subscription" : "Starts immediately"}</li>
                      <li>✓ Wallet payment — no gateway redirect</li>
                    </ul>
                    {isPermanent ? (
                      <button className="subPlan__btn subPlan__btn--disabled" disabled>
                        Admin Access Active
                      </button>
                    ) : !plan.canAfford ? (
                      <button
                        className="subPlan__btn subPlan__btn--topup"
                        onClick={() => navigate("/add-balance")}
                      >
                        Add ₹{(plan.price - walletBalance).toFixed(0)} more to buy
                      </button>
                    ) : (
                      <button
                        className="subPlan__btn subPlan__btn--buy"
                        disabled={!!buying}
                        onClick={() => setConfirm(plan)}
                      >
                        {buying === plan._id ? "Processing…" : isActive ? "Extend Plan" : "Buy Now"}
                      </button>
                    )}
                    <p className="subPlan__note">Deducted from wallet instantly</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Benefits section ── */}
          <div className="subBenefits">
            <h3 className="subBenefits__title">Why go Reseller?</h3>
            <div className="subBenefits__grid">
              <div className="subBenefit">
                <span className="subBenefit__icon">💰</span>
                <strong>Discounted Prices</strong>
                <p>Buy all game items at special reseller rates — lower than regular pricing.</p>
              </div>
              <div className="subBenefit">
                <span className="subBenefit__icon">⚡</span>
                <strong>Instant Activation</strong>
                <p>Pay from your wallet and reseller access activates immediately.</p>
              </div>
              <div className="subBenefit">
                <span className="subBenefit__icon">🔁</span>
                <strong>Stack & Extend</strong>
                <p>Already subscribed? Buy again to extend your access from the current expiry date.</p>
              </div>
              <div className="subBenefit">
                <span className="subBenefit__icon">🛡️</span>
                <strong>Secure Wallet Payment</strong>
                <p>No payment gateway redirects. Balance is deducted instantly and safely.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
